import { type Correction, type Comment } from "@/queries/formality-request";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import { Heading, TableData, Badge } from "@elaniintc/goes-design-system";
import { tableColumns, type Fields } from "../../form-correction-table/index";
import { useCallback, useMemo, useState } from "react";
import DenyReasonModal from "../../deny-reason-modal";
import { cn } from "@/utils/theme";
import FieldHistoryModal from "../../field-history-modal";
import { useCorrectionForm } from "../context";

interface FormCorrectionSectionListProps {
  formAssigment?: FormDetailResponse["form_assignments"][number];
  stepTitle: string;
  stepNumber: number;
  data: FormDetailResponse["steps"][number]["sections"];
  currentGroupId?: string | null;
  isReadOnly?: boolean;
  existingCorrection?: Correction;
  applicant?: {
    fullName: string;
    date?: string | null;
  };
  sectionStartIndex: number;
  fieldStartIndex: number;
}

export const FormCorrectionSectionList = ({
  stepTitle,
  stepNumber,
  data,
  currentGroupId,
  isReadOnly = false,
  existingCorrection,
  applicant,
  formAssigment,
  sectionStartIndex,
  fieldStartIndex,
}: FormCorrectionSectionListProps) => {
  const [tempField, setTempField] = useState<string | null>(null);
  const [modalState, setModalState] = useState<
    "idle" | "deny-reason" | "history"
  >("idle");
  const [readOnlyComments, setReadOnlyComments] = useState<Comment[]>([]);
  const [fieldSelected, setFieldSelected] = useState<Fields | null>(null);
  const {
    lockedApprovedFields,
    corrections,
    setCorrections,
    groupsCount,
    nonPreventableFields,
  } = useCorrectionForm();
  const onShowFieldHistory = useCallback(
    (field: Fields) => {
      setFieldSelected(field);
      setModalState("history");
    },
    [setFieldSelected],
  );

  const onCorrection = useCallback(
    (fieldId: string, status: "DENIED" | "APPROVED") => {
      if (isReadOnly) return;

      if (
        status === "DENIED" &&
        !currentGroupId &&
        lockedApprovedFields?.has(fieldId)
      ) {
        return;
      }

      if (status === "DENIED") {
        const prevValue = corrections.get(fieldId);
        if (prevValue?.reason) {
          setCorrections((prev) => {
            const newMap = new Map(prev);
            newMap.set(fieldId, { status, reason: prevValue.reason });
            return newMap;
          });
          return;
        }

        setTempField(fieldId);
        setModalState("deny-reason");
        return;
      }
      setCorrections((prev) => {
        const newMap = new Map(prev);
        const prevValue = newMap.get(fieldId);
        if (prevValue?.status === "DENIED") {
          newMap.set(fieldId, { status, reason: prevValue.reason });
          return newMap;
        }
        if (prevValue?.status === "APPROVED") {
          return newMap;
        }
        newMap.set(fieldId, { status });
        return newMap;
      });
      setTempField(null);
      setModalState("idle");
    },
    [
      corrections,
      setCorrections,
      isReadOnly,
      lockedApprovedFields,
      currentGroupId,
    ],
  );

  const sectionsWithData = useMemo(() => {
    let fieldCount = 0;
    return data.map((section, localIndex) => {
      const visibleFields = section.fields.filter((field) => {
        const isConditionalField = (field as { is_conditional?: boolean })
          .is_conditional;
        if (
          isConditionalField &&
          (!field.filled_fields || field.filled_fields.length === 0)
        ) {
          return false;
        }
        return true;
      });
      const sectionFieldStart = fieldStartIndex + fieldCount;
      fieldCount += visibleFields.length;
      return {
        section,
        globalSectionIndex: sectionStartIndex + localIndex + 1,
        visibleFields,
        sectionFieldStart,
      };
    });
  }, [data, sectionStartIndex, fieldStartIndex]);

  const openCorrectionModal = useCallback(
    (fieldId: string) => {
      if (isReadOnly && existingCorrection) {
        const fieldCorrection = existingCorrection.field_corrections?.find(
          (fc) => fc.filled_field_id === fieldId,
        );
        if (fieldCorrection?.comments) {
          if (Array.isArray(fieldCorrection.comments)) {
            setReadOnlyComments(fieldCorrection.comments);
          }
        }
      }
      setTempField(fieldId);
      setModalState("deny-reason");
    },
    [isReadOnly, existingCorrection],
  );

  const allSectionColumns = useMemo(() => {
    return sectionsWithData.map(({ sectionFieldStart }) =>
      tableColumns({
        onCorrection,
        corrections,
        currentGroupId,
        lockedApprovedFields,
        onShowFieldHistory,
        onOpenCorrectionModal: openCorrectionModal,
        isReadOnly,
        existingCorrection,
        groupsCount,
        nonPreventableFields,
        fieldStartIndex: sectionFieldStart,
      }),
    );
  }, [
    sectionsWithData,
    nonPreventableFields,
    onCorrection,
    corrections,
    currentGroupId,
    lockedApprovedFields,
    isReadOnly,
    existingCorrection,
    groupsCount,
    onShowFieldHistory,
    openCorrectionModal,
  ]);

  const defaultReason = useMemo(() => {
    if (isReadOnly && readOnlyComments.length > 0) {
      return readOnlyComments[0].text;
    }
    return tempField ? corrections.get(tempField)?.reason || "" : "";
  }, [isReadOnly, readOnlyComments, tempField, corrections]);

  return (
    <>
      {modalState === "deny-reason" && (
        <DenyReasonModal
          isOpen={modalState === "deny-reason"}
          onClose={() => {
            setModalState("idle");
            setTempField(null);
            setReadOnlyComments([]);
          }}
          defaultReason={defaultReason}
          onAccept={(reason) => {
            if (isReadOnly) {
              // En modo solo lectura, solo cerrar el modal
              setModalState("idle");
              setTempField(null);
              setReadOnlyComments([]);
              return;
            }

            if (tempField) {
              setCorrections((prev) => {
                const newMap = new Map(prev);
                newMap.set(tempField, {
                  status: "DENIED",
                  reason: reason.description,
                });
                return newMap;
              });
            }
            setModalState("idle");
            setTempField(null);
          }}
          readOnly={isReadOnly}
        />
      )}

      {modalState === "history" && fieldSelected && (
        <FieldHistoryModal
          isOpen={modalState === "history"}
          applicant={applicant}
          onClose={() => {
            setModalState("idle");
            setFieldSelected(null);
          }}
          formAssigment={formAssigment}
          fieldSelected={fieldSelected}
        />
      )}

      <div className="border-[0.5px] border-neutral-quiet rounded-2xl overflow-hidden w-full">
        <div className="p-3 bg-neutral-object border-b border-neutral-quiet flex items-center justify-between gap-2">
          <Heading variant="h6">{stepTitle}</Heading>
          <Badge
            variant="success"
            shape="pill"
            fill="ghost"
            size="sm"
            className="shrink-0"
          >
            Paso {stepNumber}
          </Badge>
        </div>
        {sectionsWithData.map(
          ({ section, globalSectionIndex, visibleFields }, localIndex) => (
            <div key={section.id}>
              <div className="flex items-center justify-between py-2.5 px-3 bg-neutral-object-accent-silent">
                <h6 className="font-semibold text-body-loud text-xs uppercase">
                  {section.title}
                </h6>
                <Badge
                  variant="action"
                  shape="pill"
                  fill="ghost"
                  size="sm"
                  className="shrink-0"
                >
                  Sección {globalSectionIndex}
                </Badge>
              </div>
              <TableData
                columns={allSectionColumns[localIndex]}
                data={visibleFields}
                stripped
                emptyState={<span></span>}
                wrapperClassName={cn(
                  "[&_thead]:hidden overflow-x-hidden",
                  localIndex === sectionsWithData.length - 1 &&
                    "rounded-b-2xl shadow-custom",
                )}
              />
            </div>
          ),
        )}
      </div>
    </>
  );
};
